import { createFileRoute } from '@tanstack/react-router';
import { type FormEvent, useState } from 'react';
import { useCreateProject, useProjects } from '#features/projects/api';
import { StateEditor } from '#features/projects/components/state-editor';
import { Button } from '#components/ui/button';
import { useCanWrite } from '#lib/auth/vitto-session';

const field = 'rounded-sm border border-input px-2 py-1 text-sm bg-background';

const ProjectsSettings = () => {
  const projects = useProjects();
  const create = useCreateProject();
  const canWrite = useCanWrite();
  const [form, setForm] = useState({ key: '', name: '' });
  const [openKey, setOpenKey] = useState<string | null>(null);

  const submit = (event: FormEvent): void => {
    event.preventDefault();
    if (form.key.trim() === '' || form.name.trim() === '') return;
    create.mutate(
      { key: form.key.trim().toUpperCase(), name: form.name.trim() },
      { onSuccess: () => setForm({ key: '', name: '' }) },
    );
  };

  return (
    <main className="mx-auto max-w-2xl p-8">
      <h1 className="mb-4 text-2xl">Projects</h1>
      {canWrite ? (
        <form onSubmit={submit} className="mb-6 flex gap-2">
          <input
            aria-label="Key"
            placeholder="VIT"
            value={form.key}
            onChange={(event) => setForm((prev) => ({ ...prev, key: event.target.value }))}
            className={`${field} w-24`}
          />
          <input
            aria-label="Project name"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            className={`${field} flex-1`}
          />
          <Button type="submit" size="sm">
            Create
          </Button>
        </form>
      ) : null}
      {create.isError ? (
        <p className="mb-4 text-sm text-destructive-text">{create.error.message}</p>
      ) : null}

      <ul className="divide-y divide-border">
        {(projects.data ?? []).map((project) => (
          <li key={project.id} className="py-3">
            <div className="flex items-center justify-between text-sm">
              <span>
                <span className="font-mono text-xs text-muted-foreground">{project.key}</span>{' '}
                {project.name}
              </span>
              {canWrite ? (
                <Button
                  type="button"
                  variant="link"
                  size="inline"
                  onClick={() =>
                    setOpenKey((current) => (current === project.key ? null : project.key))
                  }
                >
                  Statuses
                </Button>
              ) : null}
            </div>
            {openKey === project.key && canWrite ? <StateEditor projectKey={project.key} /> : null}
          </li>
        ))}
      </ul>
    </main>
  );
};

export const Route = createFileRoute('/_authed/settings/projects')({ component: ProjectsSettings });
